import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Activity, TrendingUp, Dumbbell, Flame, RotateCcw, Zap, Play, Clock, Target, ChevronRight, Calendar, Award, Droplets, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import HydrationTracker from "@/components/HydrationTracker";
import WorkoutGenerator from "@/components/WorkoutGenerator";
import OnboardingFlow from "@/components/OnboardingFlow";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import WeeklyPlanCard from "@/components/WeeklyPlanCard";
import MangaCommunityBanner from "@/components/MangaCommunityBanner";
import LaunchCountdown from "@/components/LaunchCountdown";
import KingOfTheCourtCard from "@/components/KingOfTheCourtCard";
import RivalCard from "@/components/RivalCard";
import SeasonalFocusCard from "@/components/SeasonalFocusCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [stats, setStats] = useState({
    totalWorkouts: 0,
    weekWorkouts: 0,
    verticalProgress: 0,
    currentVertical: 0
  });
  const [jumpProgress, setJumpProgress] = useState([]);
  const [recentWorkouts, setRecentWorkouts] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);

  // Check onboarding status when user loads
  useEffect(() => {
    if (user && user.onboarding_completed === false) {
      setOnboardingOpen(true);
    }
  }, [user]);

  const handleOnboardingComplete = (opts = {}) => {
    setOnboardingOpen(false);
    // Refresh user from backend so onboarding_completed flag updates
    fetchProfile();
    if (opts.navigateTo) {
      navigate(opts.navigateTo);
    } else {
      toast.success("You're all set. Time to train.");
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch (e) {
      console.warn('Could not refresh profile:', e.message);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchDashboardData();
    fetchRecommendation();
  }, []);

  const fetchRecommendation = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/workout/recommendation`, { headers });
      setRecommendation(response.data);
    } catch (error) {
      console.error('Error fetching recommendation:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [workoutsRes, jumpsRes] = await Promise.all([
        axios.get(`${API}/workouts`, { headers }),
        axios.get(`${API}/jumps/stats`, { headers })
      ]);

      const workouts = workoutsRes.data;
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const weekWorkouts = workouts.filter(w => new Date(w.date) >= weekAgo).length;

      const jumpData = jumpsRes.data;
      
      setStats({
        totalWorkouts: workouts.length,
        weekWorkouts,
        verticalProgress: jumpData.improvement || 0,
        currentVertical: jumpData.current || 0
      });

      if (jumpData.progress) {
        const chartData = jumpData.progress.map(p => ({
          date: new Date(p.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          inches: p.height_inches
        }));
        setJumpProgress(chartData);
      }

      setRecentWorkouts(workouts.slice(-5).reverse());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetProgress = async () => {
    if (!window.confirm('Are you sure you want to reset ALL your progress? This will delete all workouts, jump data, photos, and badges. This action cannot be undone.')) {
      return;
    }
    
    setResetting(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/users/reset_progress`, {}, { headers });
      
      setStats({ totalWorkouts: 0, weekWorkouts: 0, verticalProgress: 0, currentVertical: 0 });
      setJumpProgress([]);
      setRecentWorkouts([]);
      
      const userData = JSON.parse(localStorage.getItem('user'));
      if (userData) {
        userData.baseline_completed = false;
        userData.current_week = 0;
        userData.current_phase = 0;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      }
      
      toast.success('All progress has been reset! Start fresh!');
    } catch (error) {
      console.error('Error resetting progress:', error);
      toast.error('Failed to reset progress');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
          <div className="text-orange-500 text-lg font-heading font-bold uppercase tracking-wide">{t('loading')}</div>
        </div>
      </div>
    );
  }

  const statCards = [
    { 
      icon: Activity, 
      label: t('total_workouts'), 
      value: stats.totalWorkouts,
      color: "orange",
      testId: "total-workouts",
      link: "/sessions"
    },
    { 
      icon: Flame, 
      label: t('this_week'), 
      value: stats.weekWorkouts,
      color: "red",
      testId: "week-workouts",
      link: "/engine"
    },
    { 
      icon: TrendingUp, 
      label: t('vertical_gained'), 
      value: `+${stats.verticalProgress.toFixed(1)}"`,
      color: "emerald",
      testId: "vertical-progress",
      link: "/vertical"
    },
    { 
      icon: Dumbbell, 
      label: t('current_vertical'), 
      value: stats.currentVertical > 0 ? `${stats.currentVertical}"` : "-",
      color: "blue",
      testId: "current-vertical",
      link: "/vertical"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] p-4 md:p-6 lg:p-8 relative overflow-x-hidden w-full max-w-full">
      {/* Subtle noise overlay */}
      <div className="noise-overlay" />
      
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-7xl mx-auto space-y-8 min-w-0"
      >
        {/* Email verification banner (non-blocking — only for unverified users) */}
        <EmailVerificationBanner user={user} />

        {/* Launch countdown — beta closes June 22, 2026 */}
        <LaunchCountdown />

        {/* Community Manga Challenge banner */}
        <MangaCommunityBanner />

        {/* AI Weekly Workout Plan + Plateau Breaker */}
        <motion.div variants={itemVariants}>
          <WeeklyPlanCard />
        </motion.div>

        {/* Seasonal Focus — auto-adjusts training based on summer/fall/winter/spring */}
        <motion.div variants={itemVariants}>
          <SeasonalFocusCard />
        </motion.div>

        {/* King of the Court — weekly tournament */}
        <motion.div variants={itemVariants}>
          <KingOfTheCourtCard />
        </motion.div>

        {/* Weekly Rival head-to-head — auto-pairs after joining a team */}
        <motion.div variants={itemVariants}>
          <RivalCard />
        </motion.div>

        {/* Header - Massive Anton typography */}
        <motion.div variants={itemVariants} className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FF4500] mb-2">{t('your_progress')}</p>
            <h1 className="text-5xl md:text-7xl font-heading text-white uppercase tracking-tight leading-none">
              {t('dashboard')}
            </h1>
          </div>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              data-testid="reset-progress-btn"
              onClick={handleResetProgress}
              disabled={resetting}
              className="bg-transparent border border-white/10 text-white/50 hover:bg-white hover:text-black font-heading uppercase tracking-widest text-xs px-6 py-3 rounded-none transition-all"
            >
              <RotateCcw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
              {resetting ? t('loading') : t('reset')}
            </Button>
          </motion.div>
        </motion.div>

        {/* Baseline Prompt */}
        <AnimatePresence>
          {user && !user.baseline_completed && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="tracing-beam"
              data-testid="baseline-prompt"
            >
              <div className="bg-gradient-to-r from-orange-600/20 via-amber-600/10 to-orange-600/20 border border-orange-500/30 p-6">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-500/20 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                      <Flame className="w-6 h-6 text-orange-500 animate-flame" />
                    </div>
                    <div>
                      <h3 className="text-xl md:text-2xl font-heading font-black text-white uppercase tracking-tight">
                        {t('start_training')}
                      </h3>
                      <p className="text-zinc-400 text-sm mt-1">We're here to make you better. Let's train.</p>
                    </div>
                  </div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={() => navigate('/baseline')}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase text-sm tracking-wide transition-colors btn-tactical"
                      data-testid="baseline-cta"
                    >
                      {t('start_workout')}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Grid - Control Room Style */}
        <motion.div variants={itemVariants} className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.06] min-w-0" data-testid="dashboard-stats">
          {statCards.map((stat, idx) => (
            <motion.div
              key={idx}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="cursor-pointer"
              onClick={() => navigate(stat.link)}
              data-testid={`stat-card-${stat.testId}`}
            >
              <div className="bg-[#0A0A0A] p-5 h-full border border-transparent hover:border-[#FF4500]/30 transition-all relative group halftone">
                <div className="flex items-center justify-between mb-4">
                  <stat.icon className={`w-5 h-5 text-[#FF4500]`} />
                  <ChevronRight className="w-4 h-4 text-white/10 group-hover:text-[#FF4500] transition-colors" />
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">{stat.label}</p>
                <p className="text-4xl md:text-5xl font-heading text-white leading-none" data-testid={stat.testId}>
                  {stat.value}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-4 min-w-0">
          {/* Vertical Progress Chart */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <Card className="bg-zinc-900/80 border border-zinc-800" data-testid="vertical-chart-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                    Vertical Jump Progress
                  </CardTitle>
                  {jumpProgress.length > 0 && (
                    <span className="text-xs text-emerald-400 font-bold">
                      +{stats.verticalProgress.toFixed(1)}"
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {jumpProgress.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={jumpProgress}>
                      <defs>
                        <linearGradient id="colorInches" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="date" 
                        stroke="#52525b" 
                        style={{ fontSize: '10px' }} 
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#52525b" 
                        style={{ fontSize: '10px' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '0',
                          color: '#fff',
                          fontSize: '12px'
                        }}
                        labelStyle={{ color: '#a1a1aa' }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="inches" 
                        stroke="#f97316" 
                        strokeWidth={2}
                        fill="url(#colorInches)"
                        dot={{ fill: '#f97316', r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: '#f97316', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex flex-col items-center justify-center text-zinc-600">
                    <TrendingUp className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">No jump data yet</p>
                    <p className="text-xs text-zinc-700 mb-3">Start logging your vertical jumps!</p>
                    <Button
                      data-testid="log-jumps-btn"
                      size="sm"
                      onClick={() => navigate('/vertical')}
                      className="bg-orange-600 hover:bg-orange-500 text-white font-bold uppercase text-xs"
                    >
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Log Jumps
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Workouts */}
          <motion.div variants={itemVariants}>
            <Card className="bg-zinc-900/80 border border-zinc-800" data-testid="recent-workouts-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading font-bold text-white uppercase tracking-tight flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Recent Workouts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentWorkouts.length > 0 ? (
                  <div className="space-y-2">
                    {recentWorkouts.map((workout, idx) => (
                      <motion.div 
                        key={workout.id} 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex justify-between items-center p-3 bg-zinc-950/50 border border-zinc-800 hover:border-zinc-700 transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                            <Dumbbell className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white">
                              {workout.drill_ids.length} Drill{workout.drill_ids.length !== 1 ? 's' : ''}
                            </p>
                            <p className="text-[10px] text-zinc-500 uppercase tracking-wide">
                              {new Date(workout.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-mono font-bold text-orange-500">
                            {Math.floor(workout.total_duration_seconds / 60)}m
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[220px] flex flex-col items-center justify-center text-zinc-600">
                    <Dumbbell className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">No workouts logged yet</p>
                    <p className="text-xs text-zinc-700 mb-3">Start training to see your history!</p>
                    <Button
                      data-testid="start-training-btn"
                      size="sm"
                      onClick={() => navigate('/engine')}
                      className="bg-blue-600 hover:bg-blue-500 text-white font-bold uppercase text-xs"
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Start Training
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Hydration Tracker */}
          <motion.div variants={itemVariants}>
            <HydrationTracker data-testid="hydration-tracker" />
          </motion.div>
        </div>

        {/* Quick Start Recommendation */}
        <AnimatePresence>
          {recommendation && recommendation.has_recommendation && (
            <motion.div
              variants={itemVariants}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-gradient-to-br from-amber-600/20 via-orange-600/10 to-red-600/20 border border-orange-500/30 overflow-hidden" data-testid="quick-start-card">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-amber-500" />
                        </div>
                        <span className="text-[10px] font-black uppercase text-amber-500 tracking-[0.2em]">Today's Quick Start</span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-heading font-black text-white uppercase tracking-tight mb-2">
                        {recommendation.drill.name}
                      </h3>
                      <p className="text-zinc-400 text-sm mb-4">{recommendation.motivation}</p>
                      
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {[
                          { icon: Target, label: "Focus", value: recommendation.focus_area, color: "orange" },
                          { icon: Clock, label: "Duration", value: `${recommendation.estimated_minutes} min`, color: "blue" },
                          { icon: Activity, label: "Sets", value: recommendation.suggested_sets, color: "emerald" }
                        ].map((item, idx) => (
                          <div key={idx} className="text-center p-3 bg-zinc-950/50 border border-zinc-800">
                            <item.icon className={`w-4 h-4 mx-auto text-${item.color}-500 mb-1`} />
                            <p className="text-sm font-bold text-white">{item.value}</p>
                            <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{item.label}</p>
                          </div>
                        ))}
                      </div>

                      {recommendation.drill.frequency_per_week && (
                        <p className="text-xs text-zinc-500">
                          <span className="text-amber-500 font-bold">{recommendation.drill.frequency_per_week}x per week</span> recommended
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-3">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          data-testid="quick-start-btn"
                          onClick={() => navigate('/engine', { state: { selectedDrill: recommendation.drill } })}
                          className="bg-amber-500 hover:bg-amber-400 text-black font-black uppercase px-8 py-6 text-base tracking-wide btn-tactical glow-orange"
                        >
                          <Play className="w-5 h-5 mr-2" />
                          {t('quick_start')}
                        </Button>
                      </motion.div>
                      <Button
                        data-testid="dashboard-generate-workout-btn"
                        onClick={() => setGeneratorOpen(true)}
                        className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wide"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Pick & Generate
                      </Button>
                      <Button
                        variant="outline"
                        onClick={fetchRecommendation}
                        className="border-zinc-700 hover:bg-zinc-800 text-zinc-400 font-bold uppercase text-xs"
                      >
                        {t('next')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Actions */}
        <motion.div variants={itemVariants}>
          <Card className="bg-gradient-to-r from-orange-600/10 via-zinc-900 to-red-600/10 border border-zinc-800">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 border border-orange-500/30 flex items-center justify-center">
                    <Flame className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-heading font-black text-white uppercase tracking-tight">{t('ready_train')}</h3>
                    <p className="text-zinc-500 text-sm">{t('todays_workout')}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button 
                      onClick={() => navigate('/schedule')}
                      variant="outline"
                      className="inline-flex items-center gap-2 px-5 py-3 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-white font-bold uppercase text-xs tracking-wide transition-colors"
                      data-testid="view-schedule-button"
                    >
                      <Calendar className="w-4 h-4" />
                      {t('schedule')}
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      onClick={() => navigate('/engine')}
                      className="inline-flex items-center gap-2 px-5 py-3 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase text-xs tracking-wide transition-colors btn-tactical"
                      data-testid="start-workout-button"
                    >
                      <Play className="w-4 h-4" />
                      {t('start_workout')}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>

      <WorkoutGenerator
        open={generatorOpen}
        onOpenChange={setGeneratorOpen}
        initialPhase={recommendation?.phase || 1}
      />

      <OnboardingFlow
        open={onboardingOpen}
        onComplete={handleOnboardingComplete}
        userName={user?.name}
      />
    </div>
  );
};

export default Dashboard;
